import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { DatePicker, Button } from 'antd';
import dayjs from 'dayjs';
import './Filter.css';

const { RangePicker } = DatePicker;

const deviceOptions = {};

export default function Filter({ deviceType, onSearch }) {
    const [range, setRange] = useState([]); // [start, end]
    const [selectedFreq, setSelectedFreq] = useState('');
    const [selectedInter, setSelectedInter] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const config = deviceOptions[deviceType] || {};
    const freqOptions = config.frequency ?? [];
    const interOptions = config.interval ?? [];

    // Two most recent months
    const monthOptions = useMemo(() => {
        const now = new Date();
        const cur = now.getMonth() + 1;
        const prev = cur === 1 ? 12 : cur - 1;
        return [`${prev}月`, `${cur}月`];
    }, []);

    // Reset when route or deviceType changes
    useEffect(() => {
        setRange([]);
        setSelectedFreq('');
        setSelectedInter('');
        setSelectedMonth('');
    }, [deviceType, location.pathname]);

    useEffect(() => {
        const urlStart = searchParams.get('start');
        const urlEnd = searchParams.get('end');
        if (!urlStart || !urlEnd) return;

        const start = dayjs(urlStart);
        const end = dayjs(urlEnd);
        if (!start.isValid() || !end.isValid()) return;

        setRange((prev) => {
            if (
                prev.length === 2 &&
                prev[0] &&
                prev[1] &&
                prev[0].isSame(start) &&
                prev[1].isSame(end)
            ) {
                return prev;
            }
            return [start, end];
        });
    }, [searchParams]);

    // Month quick-select
    const handleMonthSelect = (m) => {
        if (selectedMonth === m) {
            setSelectedMonth('');
            setRange([]);
            return;
        }
        setSelectedMonth(m);
        const monthNum = parseInt(m, 10);
        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear();
        const targetYear = monthNum > currentMonth ? currentYear - 1 : currentYear;

        const startMonthStr = String(monthNum).padStart(2, '0');
        const start = dayjs(`${targetYear}-${startMonthStr}-01T00:00`);

        const nextMonthNum = monthNum === 12 ? 1 : monthNum + 1;
        const nextYear = monthNum === 12 ? targetYear + 1 : targetYear;
        const nextMonthStr = String(nextMonthNum).padStart(2, '0');
        const end = dayjs(`${nextYear}-${nextMonthStr}-01T00:00`);

        setRange([start, end]);
    };

    // Trigger search
    const handleSearch = async () => {
        if (isSearching) return;
        if (!range.length) {
            alert('請先選擇開始與結束時間');
            return;
        }

        const filter = {
            startDate: range[0].format('YYYY-MM-DDTHH:mm'),
            endDate: range[1].format('YYYY-MM-DDTHH:mm'),
            frequency: selectedFreq,
            interval: selectedInter,
            shouldSyncUrl: true,
        };

        if (!onSearch) {
            alert('搜尋功能未實作');
            return;
        }

        setIsSearching(true);
        try {
            await onSearch(filter);
        } catch (e) {
            console.error('Search failed:', e);
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <div className="filter-container">
            {/* Date range */}
            <div className="filter-group full-row">
                <label>時間區間：</label>
                <RangePicker
                    showTime={{ format: 'HH:mm', minuteStep: 60 }}
                    format="YYYY-MM-DD HH:mm"
                    value={range}
                    onChange={(values) => setRange(values)}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Frequency */}
            {freqOptions.length > 0 && (
                <div className="filter-group full-row">
                    <label>測量頻率：</label>
                    {freqOptions.map((f) => (
                        <Button
                            key={f}
                            type={selectedFreq === f ? 'primary' : 'default'}
                            onClick={() =>
                                setSelectedFreq((prev) => (prev === f ? '' : f))
                            }
                        >
                            {f}
                        </Button>
                    ))}
                </div>
            )}

            {/* Interval */}
            {interOptions.length > 0 && (
                <div className="filter-group full-row">
                    <label>測量間隔：</label>
                    {interOptions.map((i) => (
                        <Button
                            key={i}
                            type={selectedInter === i ? 'primary' : 'default'}
                            onClick={() =>
                                setSelectedInter((prev) => (prev === i ? '' : i))
                            }
                        >
                            {i}
                        </Button>
                    ))}
                </div>
            )}

            {/* Month shortcuts */}
            <div className="filter-group full-row">
                <label>月份：</label>
                {monthOptions.map((m) => (
                    <Button
                        key={m}
                        type={selectedMonth === m ? 'primary' : 'default'}
                        onClick={() => handleMonthSelect(m)}
                    >
                        {m}
                    </Button>
                ))}

                {/* Search button */}
                <div className="filter-group search-group">
                    <Button type="primary" onClick={handleSearch} loading={isSearching}>
                        搜尋
                    </Button>
                </div>
            </div>
        </div>
    );
}
